import { BarChart3, Plus, Calculator, Eye, TrendingUp, History, GitCompare, PieChart } from "lucide-react";
import { LandingHeader, LandingFooter } from "@/components/landing";
import {
  FeatureHero,
  FeatureCTA,
  RelatedFeatures,
  Breadcrumb,
  FeatureSection,
  ProblemBlock,
  Step,
  BenefitCard,
} from "@/components/product";
import { usePageMeta } from "@/hooks/usePageMeta";
import { JsonLd } from "@/components/seo/JsonLd";

const benefits = [
  {
    icon: TrendingUp,
    title: "Marge calculée en temps réel",
    description: "Votre marge est mise à jour instantanément à chaque dépense ou affectation.",
    gradient: "from-[#10B981] to-[#059669]",
  },
  {
    icon: Eye,
    title: "Vision claire du budget",
    description: "Visualisez en un clin d'œil le budget consommé vs le budget restant.",
    gradient: "from-[#3B82F6] to-[#2563EB]",
  },
  {
    icon: GitCompare,
    title: "Comparaison entre chantiers",
    description: "Identifiez vos chantiers les plus rentables et ceux qui nécessitent attention.",
    gradient: "from-[#F59E0B] to-[#D97706]",
  },
  {
    icon: History,
    title: "Historique des évolutions",
    description: "Suivez l'évolution de votre marge dans le temps avec des graphiques détaillés.",
    gradient: "from-[#8B5CF6] to-[#7C3AED]",
  },
];

const steps = [
  {
    number: 1,
    title: "Ajoutez vos dépenses",
    description: "Factures fournisseurs, frais annexes, main d'œuvre... tout est centralisé.",
    icon: Plus,
  },
  {
    number: 2,
    title: "B8ild calcule automatiquement",
    description: "La marge est recalculée en temps réel à chaque modification.",
    icon: Calculator,
  },
  {
    number: 3,
    title: "Visualisez votre rentabilité",
    description: "Dashboard clair avec indicateurs visuels et alertes si besoin.",
    icon: Eye,
  },
];

const RentabiliteFeature = () => {
  usePageMeta({ title: "Suivi rentabilité chantier temps réel — B8ild", description: "Suivez la marge de chaque chantier en temps réel. Plus besoin d'attendre la fin du chantier pour savoir si vous gagnez ou perdez.", url: "https://b8ild.fr/product/rentabilite" });
  return (
    <div className="min-h-screen bg-[#0F172A]">
      <JsonLd data={{
        "@context": "https://schema.org",
        "@type": "WebPage",
        name: "Suivi rentabilité chantier temps réel — B8ild",
        description: "Suivez la marge de chaque chantier en temps réel. Plus besoin d'attendre la fin du chantier pour savoir si vous gagnez ou perdez.",
        url: "https://b8ild.fr/product/rentabilite",
        isPartOf: { "@type": "WebSite", name: "B8ild", url: "https://b8ild.fr" },
      }} />
      <JsonLd data={{
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Accueil", item: "https://b8ild.fr/" },
          { "@type": "ListItem", position: 2, name: "Produit", item: "https://b8ild.fr/product" },
          { "@type": "ListItem", position: 3, name: "Rentabilité temps réel", item: "https://b8ild.fr/product/rentabilite" },
        ],
      }} />
      <LandingHeader />

      {/* Breadcrumb */}
      <div className="container mx-auto px-4 pt-24">
        <Breadcrumb
          items={[
            { label: "Produit", href: "/product" },
            { label: "Rentabilité temps réel" },
          ]}
        />
      </div>

      {/* Hero */}
      <FeatureHero
        badge="Rentabilité temps réel"
        title="Votre marge, calculée"
        titleHighlight="automatiquement."
        subtitle="Fini les surprises en fin de chantier. B8ild calcule votre rentabilité à chaque dépense, à chaque affectation, en temps réel."
        icon={BarChart3}
        gradient="from-[#10B981] to-[#059669]"
      />

      {/* Problem */}
      <FeatureSection variant="dark">
        <ProblemBlock
          stat="68%"
          text="des professionnels du BTP découvrent qu'ils ont perdu de l'argent sur un chantier... après l'avoir terminé."
          source="Étude FFB 2023"
        />
      </FeatureSection>

      {/* How it works */}
      <FeatureSection
        title="Comment ça marche ?"
        subtitle="En 3 étapes simples, visualisez votre rentabilité"
      >
        <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
          {steps.map((step) => (
            <Step key={step.number} {...step} />
          ))}
        </div>
      </FeatureSection>

      {/* Benefits */}
      <FeatureSection
        variant="dark"
        title="Ce que vous gagnez"
        subtitle="Des bénéfices concrets pour votre activité"
      >
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 max-w-5xl mx-auto">
          {benefits.map((benefit, index) => (
            <BenefitCard key={index} {...benefit} />
          ))}
        </div>
      </FeatureSection>

      {/* Dashboard Preview */}
      <FeatureSection title="Un dashboard clair et actionnable">
        <div className="max-w-4xl mx-auto">
          {/* Mock dashboard */}
          <div className="rounded-2xl p-[1px] bg-gradient-to-b from-white/[0.15] to-white/[0.02]">
            <div className="rounded-2xl bg-[#0F0F14]/90 backdrop-blur-xl overflow-hidden">
              {/* Window chrome */}
              <div className="flex items-center gap-2 px-4 py-3 bg-white/[0.02] border-b border-white/[0.05]">
                <div className="flex gap-1.5">
                  <div className="h-2.5 w-2.5 rounded-full bg-white/20" />
                  <div className="h-2.5 w-2.5 rounded-full bg-white/20" />
                  <div className="h-2.5 w-2.5 rounded-full bg-white/20" />
                </div>
                <div className="flex-1 flex justify-center">
                  <div className="px-3 py-1 rounded-md bg-white/[0.03] text-[11px] text-white/30 font-mono">
                    app.b8ild.fr/dashboard
                  </div>
                </div>
              </div>

              {/* Content */}
              <div className="p-6 space-y-6">
                {/* KPI Cards */}
                <div className="grid grid-cols-4 gap-4">
                  <div className="rounded-xl bg-gradient-to-br from-white/[0.04] to-transparent border border-white/[0.04] p-4">
                    <p className="text-xs uppercase tracking-wider text-white/30 font-medium">Devis HT</p>
                    <p className="text-2xl font-bold text-white mt-1">45 000 €</p>
                  </div>
                  <div className="rounded-xl bg-gradient-to-br from-white/[0.04] to-transparent border border-white/[0.04] p-4">
                    <p className="text-xs uppercase tracking-wider text-white/30 font-medium">Dépensé</p>
                    <p className="text-2xl font-bold text-[#F59E0B] mt-1">28 350 €</p>
                  </div>
                  <div className="rounded-xl bg-gradient-to-br from-white/[0.04] to-transparent border border-white/[0.04] p-4">
                    <p className="text-xs uppercase tracking-wider text-white/30 font-medium">Budget dispo</p>
                    <p className="text-2xl font-bold text-white mt-1">16 650 €</p>
                  </div>
                  <div className="rounded-xl bg-gradient-to-br from-[#10B981]/10 to-transparent border border-[#10B981]/20 p-4">
                    <p className="text-xs uppercase tracking-wider text-[#10B981]/70 font-medium">Marge</p>
                    <p className="text-2xl font-bold text-[#10B981] mt-1">+23.5%</p>
                  </div>
                </div>

                {/* Chart placeholder */}
                <div className="rounded-xl bg-white/[0.02] border border-white/[0.04] p-4">
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-sm text-white/50 font-medium">Évolution de la marge</span>
                    <span className="text-xs px-2 py-0.5 rounded-full bg-[#10B981]/10 text-[#10B981] font-medium">+5% ce mois</span>
                  </div>
                  <div className="flex items-end justify-between h-24 gap-2">
                    {[35, 40, 38, 45, 50, 48, 55, 52, 60, 58, 65, 70].map((h, i) => (
                      <div
                        key={i}
                        className="flex-1 rounded-t bg-gradient-to-t from-[#10B981]/40 to-[#10B981]/80"
                        style={{ height: `${h}%` }}
                      />
                    ))}
                  </div>
                  <div className="flex justify-between mt-2 text-[10px] text-white/30">
                    <span>Jan</span>
                    <span>Fév</span>
                    <span>Mar</span>
                    <span>Avr</span>
                    <span>Mai</span>
                    <span>Juin</span>
                    <span>Juil</span>
                    <span>Août</span>
                    <span>Sep</span>
                    <span>Oct</span>
                    <span>Nov</span>
                    <span>Déc</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </FeatureSection>

      {/* Pourquoi c'est important */}
      <section className="py-16 bg-[#0A0A0F]">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-2xl font-bold text-white mb-6">
              Marc pensait gagner 50 000€. Il en a gagné 15 000.
            </h2>
            <div className="space-y-4 text-white/60 leading-relaxed">
              <p>
                Marc est plombier-chauffagiste. Il fait 250 000 EUR de CA par an. 5 chantiers en parallele. Il pensait tourner a 20% de marge. En realite ? 6%. Soit 15 000 EUR de benefice net au lieu des 50 000 EUR qu'il imaginait.
              </p>
              <p>
                Le probleme : il decouvrait ses marges reelles chez le comptable, 3 mois apres la cloture. Un chantier PAC a 14 000 EUR lui a coute 15 200 EUR en realite — 1 200 EUR de perte seche. Les heures sup' de son apprenti n'etaient pas comptees. Les allers-retours fournisseur non plus.
              </p>
              <p>
                Avec un suivi en temps reel, Marc aurait vu la derive au bout de 5 jours, pas au bout de 5 mois. Il aurait ajuste ses achats ou negocie un avenant. Les artisans qui suivent leur rentabilite chantier par chantier gagnent en moyenne 3 a 7 points de marge nette en un an.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Questions frequentes */}
      <section className="py-16 bg-[#08080C]">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-2xl font-bold text-white mb-8">
              Questions frequentes
            </h2>
            <div className="space-y-6">
              {[
                {
                  q: "Comment savoir si un chantier est rentable ?",
                  a: "Comparez le budget previsionnel (devis HT) aux couts reels engages : materiaux, main d'oeuvre, sous-traitance, frais annexes. Si les couts depassent 80% du devis avant la fin des travaux, le chantier risque d'etre deficitaire. B8ild fait ce calcul en continu — vous voyez le % de marge a jour en temps reel.",
                },
                {
                  q: "Quel est le taux de marge moyen dans le BTP ?",
                  a: "La marge brute moyenne se situe entre 25% et 50% selon le type de travaux. La marge nette (apres frais generaux) tombe entre 3% et 8%. En renovation energetique (PAC, isolation), les marges brutes montent a 30-50%. En gros oeuvre, elles descendent a 25-35%. Le depannage est le plus rentable : 50-70% de marge brute.",
                },
                {
                  q: "Comment ameliorer la rentabilite d'un chantier ?",
                  a: "Trois leviers : 1) Negocier les materiaux (2-5% d'economie moyenne). 2) Suivre les heures reelles vs les heures prevues chaque semaine. 3) Detecter les derives de cout avant qu'elles ne s'accumulent. Un retard de 3 jours sur un chantier a 60 000 EUR mange 800 a 1 500 EUR de marge nette.",
                },
              ].map((faq, i) => (
                <div key={i} className="border-b border-white/[0.06] pb-6">
                  <h3 className="text-lg font-medium text-white mb-3">{faq.q}</h3>
                  <p className="text-white/50 leading-relaxed">{faq.a}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <FeatureCTA
        title="Suivez votre rentabilité en temps réel"
        subtitle="Ne découvrez plus jamais une perte après coup."
      />

      {/* Related Features */}
      <RelatedFeatures currentFeatureId="rentabilite" />

      <LandingFooter />
    </div>
  );
};

export default RentabiliteFeature;
